import React, { useState, useEffect } from 'react';
import { isToday } from 'date-fns';
import { format } from 'date-fns';
import {
  Panel,
  Row,
  Col,
  Stack,
  Button,
  Modal,
  Calendar,
  Tabs,
  Tab,
  SelectPicker,
  Placeholder,
  ButtonGroup
} from 'rsuite';
import Chart from 'react-apexcharts';
import { useTranslation } from 'react-i18next';
import {
  zoneByFloor,
  teamWorkspaces,
  getZoneBookingOccupationPerDate,
  getFutureOccupation,
  getFutureOccupationByfloor
} from '@/services/dashboard.service';
import { LuCalendarDays } from 'react-icons/lu';

const getCurrentWeek = () => {
  const today = new Date();
  return generateWeek(today);
};

const generateWeek = date => {
  const start = new Date(date);
  start.setDate(start.getDate() - start.getDay() + 1); // Monday
  const week = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    week.push(d.toISOString().split('T')[0]);
  }
  console.log('Generated week:', week);
  return week;
};

const ReportingDashboard = () => {
  const { t, i18n } = useTranslation();
  const [bookingStatsByDate, setBookingStatsByDate] = useState([]);
  const [deskStats, setDeskStats] = useState({
    bookingSeries: [],
    occupationSeries: [],
    options: {}
  });
  const [selectedZone, setSelectedZone] = useState(null);
  const [chartData, setChartData] = useState({ desks: [], meetingRooms: [] });
  const [heatmapBookingSeries, setHeatmapBookingSeries] = useState([]);
  const [heatmapOccupationSeries, setHeatmapOccupationSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deskStatsLoading, setDeskStatsLoading] = useState(true);
  const [floorDataLoading, setFloorDataLoading] = useState(true);
  const [selectedWeek, setSelectedWeek] = useState(getCurrentWeek());
  const [showCalendar, setShowCalendar] = useState(false);
  const [data, setData] = useState([]);
  const [selectedFloor, setSelectedFloor] = useState('');
  const [series, setSeries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [seriesfloor, setSeriesfloor] = useState([]);
  const [categoriesfloor, setCategoriesfloor] = useState([]);

  useEffect(() => {
    fetchWorkspacesBar();
    fetchData();
    fetchDataFloor();
  }, []);

  useEffect(() => {
    fetchBooking();
    fetchDeskStats();
  }, [selectedWeek]);

  const fetchDeskStats = async () => {
    setDeskStatsLoading(true);
    try {
      const startDate = selectedWeek[0]; // First day of the week
      const endDate = selectedWeek[6]; // Last day of the week

      // Validate dates
      console.log('fetchDeskStats date range:', { startDate, endDate });
      if (!startDate || !endDate || isNaN(new Date(startDate)) || isNaN(new Date(endDate))) {
        console.error('Invalid date range in fetchDeskStats:', { startDate, endDate });
        return;
      }

      const response = await zoneByFloor(startDate, endDate);

      const bookingMap = new Map();
      const occupationMap = new Map();

      if (response && response.status === 200 && response.data) {
        Object.keys(response.data).forEach(date => {
          const floorData = response.data[date];
          for (const floorName in floorData) {
            const floor = floorData[floorName];
            const booking = parseFloat(floor.bookingRate || 0);
            const occupation = parseFloat(floor.occupationRate || 0);

            // Booking
            if (!bookingMap.has(floorName)) bookingMap.set(floorName, []);
            bookingMap.get(floorName).push({ x: date, y: booking });

            // Occupation
            if (!occupationMap.has(floorName)) occupationMap.set(floorName, []);
            occupationMap.get(floorName).push({ x: date, y: occupation });
          }
        });
      } else {
        console.warn('No valid data returned from zoneByFloor:', response);
      }

      const bookingSeries = Array.from(bookingMap.entries()).map(([name, data]) => ({
        name,
        data
      }));
      const occupationSeries = Array.from(occupationMap.entries()).map(([name, data]) => ({
        name,
        data
      }));

      setDeskStats({
        bookingSeries,
        occupationSeries,
        options: {
          chart: {
            type: 'bar',
            stacked: true,
            height: 350,
            toolbar: { show: true }
          },
          xaxis: {
            type: 'category',
            categories: selectedWeek
          },
          yaxis: {
            title: { text: 'Rate' },
            labels: {
              formatter: val => `${val}%`
            },
            max: undefined
          },
          legend: { position: 'top' },
          plotOptions: {
            bar: {
              columnWidth: '60%'
            }
          },
          tooltip: {
            y: {
              formatter: val => `${val.toFixed(2)}%`
            }
          },
          colors: ['#4c8dff', '#ff861c', '#9C27B0', '#4CAF50', '#FFC107', '#E91E63']
        }
      });
    } catch (error) {
      console.error('Error fetching desk stats by date range:', error);
    } finally {
      setDeskStatsLoading(false);
    }
  };

  const fetchDataFloor = async () => {
    setFloorDataLoading(true);
    try {
      const response = await getFutureOccupationByfloor();
      console.log('API Response:', JSON.stringify(response, null, 2));

      // Ensure response.data is an array
      const data = Array.isArray(response.data) ? response.data : [];
      console.log('Data:', JSON.stringify(data, null, 2));

      if (!data.length) {
        console.warn('No floor data available');
        setSeriesfloor([]);
        setCategoriesfloor([]);
        return;
      }

      // Group by floor name
      const floorMap = {};
      data.forEach(entry => {
        if (!entry.date || !Array.isArray(entry.floors)) {
          console.warn('Invalid entry structure:', JSON.stringify(entry, null, 2));
          return;
        }
        const date = entry.date;
        entry.floors.forEach(floorEntry => {
          const floorName = floorEntry.floor || 'Unknown Floor';
          if (!floorMap[floorName]) {
            floorMap[floorName] = {};
          }
          floorMap[floorName][date] = Number(floorEntry.booking) || 0;
        });
      });

      console.log('Floor Map:', JSON.stringify(floorMap, null, 2));

      // Convert Set to array and sort
      const allDatesSet = new Set(data.map(item => item.date).filter(Boolean));
      const allDates = Array.from(allDatesSet).sort();
      console.log('All Dates:', allDates);

      const finalSeries = Object.keys(floorMap).map(floor => ({
        name: floor,
        data: allDates.map(date => Math.round((floorMap[floor][date] || 0) * 100))
      }));

      console.log('Seriesfloor:', JSON.stringify(finalSeries, null, 2));
      console.log('Categoriesfloor:', allDates);

      setCategoriesfloor(allDates);
      setSeriesfloor(finalSeries);
    } catch (error) {
      console.error('Error fetching floor data:', error);
      setSeriesfloor([]);
      setCategoriesfloor([]);
    } finally {
      setFloorDataLoading(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await getFutureOccupation();

      // 🧩 fonction pour extraire le numéro de l'étage
      const parseFloorNumber = floorName => {
        if (!floorName || typeof floorName !== 'string') return 0;
        const lower = floorName.toLowerCase();
        const match = floorName.match(/-?\d+/);
        if (match) return parseInt(match[0], 10);
        if (lower.includes('parking')) return -1;
        if (lower.includes('basement') || lower.includes('b')) return -1;
        if (lower.includes('ground') || lower.includes('rez') || lower.includes('rdc')) return 0;
        return 0;
      };

      // 🧠 trier les floors du parking (-1) vers les étages supérieurs
      const sortedData = res.data
        .map(floorEntry => ({
          ...floorEntry,
          dates: floorEntry.dates.sort((a, b) => new Date(a.date) - new Date(b.date))
        }))
        .sort((a, b) => parseFloorNumber(a.floor) - parseFloorNumber(b.floor));

      // ✅ sauvegarde dans ton state
      setData(sortedData);

      if (sortedData.length > 0) {
        setSelectedFloor(sortedData[0].floor);
      }

      console.log(
        'Floors triés:',
        sortedData.map(f => f.floor)
      );
      console.log('Floor sélectionné par défaut:', sortedData[0].floor);
    } catch (error) {
      console.error('Erreur lors du fetch future occupation:', error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedFloor || data.length === 0) return;

    const floorData = data.find(f => f.floor === selectedFloor);
    console.log('🔍 selectedFloor:', selectedFloor);
    console.log('🔍 floorData:', floorData);

    if (!floorData) return;

    const dateLabels = floorData.dates.map(d => d.date);

    const zoneMap = {};
    floorData.dates.forEach(d => {
      d.zones.forEach(zone => {
        if (!zoneMap[zone.name]) {
          zoneMap[zone.name] = {};
        }
        zoneMap[zone.name][d.date] = zone.occupation;
      });
    });

    const chartSeries = Object.keys(zoneMap).map(zoneName => ({
      name: zoneName,
      data: dateLabels.map(date => Math.round(zoneMap[zoneName][date] || 0))
    }));

    setSeries(chartSeries);
    setCategories(dateLabels);
  }, [data, selectedFloor]);

  const fetchWorkspacesBar = async () => {
    try {
      const response = await teamWorkspaces();
      if (response.status === 200) {
        const rawData = response.data.data;
        const categories = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi'];
        const desksData = categories.map(day => {
          const entry = rawData.find(item => item.day === day);
          return entry ? entry.desks : 0;
        });
        const meetingRoomsData = categories.map(day => {
          const entry = rawData.find(item => item.day === day);
          return entry ? entry.meeting_rooms : 0;
        });

        setChartData({ desks: desksData, meetingRooms: meetingRoomsData });
      }
    } catch (error) {
      console.error('Error fetching workspace utilization data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBooking = async () => {
    try {
      const startDate = selectedWeek[0];
      const endDate = selectedWeek[6];

      console.log('fetchBooking date range:', { startDate, endDate });
      if (!startDate || !endDate || isNaN(new Date(startDate)) || isNaN(new Date(endDate))) {
        console.error('Invalid date range in fetchBooking:', { startDate, endDate });
        return;
      }

      const res = await getZoneBookingOccupationPerDate(startDate, endDate);

      // 🟦 Étape 1 : trier les floors par numéro (-1, 0, 1, 2, etc.)
      Object.keys(res).forEach(date => {
        const floorEntries = Object.entries(res[date]);

        const sortedFloors = floorEntries.sort(([floorA], [floorB]) => {
          const numA = parseInt(floorA.match(/-?\d+/)?.[0] || 0, 10);
          const numB = parseInt(floorB.match(/-?\d+/)?.[0] || 0, 10);
          return numA - numB;
        });

        // Remplace par l’ordre trié
        res[date] = Object.fromEntries(sortedFloors);
      });

      // 🟦 Étape 2 : ton code reste identique après
      const allZonesMap = new Map();

      Object.keys(res).forEach(date => {
        const floorData = res[date];
        Object.keys(floorData).forEach(floor => {
          floorData[floor].zones.forEach(zone => {
            const zoneLabel = `${zone.zoneName} (${floor})`;
            if (!allZonesMap.has(zoneLabel)) {
              allZonesMap.set(zoneLabel, {
                bookingData: [],
                occupationData: []
              });
            }
            const zoneData = allZonesMap.get(zoneLabel);
            zoneData.bookingData.push({ x: date, y: parseFloat(zone.bookingRate || 0) });
            zoneData.occupationData.push({ x: date, y: parseFloat(zone.occupationRate || 0) });
          });
        });
      });

      const bookingSeries = [];
      const occupationSeries = [];

      for (const [zoneLabel, { bookingData, occupationData }] of allZonesMap.entries()) {
        const completedBooking = selectedWeek.map(date => {
          const found = bookingData.find(d => d.x === date);
          return { x: date, y: found ? found.y : 0 };
        });

        const completedOccupation = selectedWeek.map(date => {
          const found = occupationData.find(d => d.x === date);
          return { x: date, y: found ? found.y : 0 };
        });

        bookingSeries.push({ name: zoneLabel, data: completedBooking });
        occupationSeries.push({ name: zoneLabel, data: completedOccupation });
      }

      setHeatmapBookingSeries(bookingSeries);
      setHeatmapOccupationSeries(occupationSeries);
    } catch (err) {
      console.error('Erreur fetchBooking:', err);
    } finally {
      setLoading(false);
    }
  };

  const heatmapOptions = {
    chart: { type: 'heatmap', toolbar: { show: true } },
    dataLabels: { enabled: true },
    xaxis: { type: 'category', categories: selectedWeek },
    plotOptions: {
      heatmap: {
        shadeIntensity: 0.5,
        radius: 0,
        useFillColorAsStroke: true,
        colorScale: {
          ranges: [
            { from: 0, to: 0.3, color: '#90ee7e', name: 'low [0, 0.3]' },
            { from: 0.3, to: 0.6, color: '#fdd835', name: 'medium [0.31 , 0.7]' },
            { from: 0.6, to: 1, color: '#f4511e', name: 'high [0.71 , 1]' }
          ]
        }
      }
    }
  };

  const options = {
    chart: {
      type: 'line',
      toolbar: { show: false }
    },
    stroke: {
      width: 2,
      curve: 'smooth'
    },
    markers: {
      size: 4
    },
    xaxis: {
      categories,
      title: { text: 'Date' }
    },
    yaxis: {
      title: { text: 'Booking %' },
      max: 100
    },
    tooltip: {
      y: {
        formatter: val => `${val}%`
      }
    },
    legend: {
      position: 'bottom'
    }
  };

  const optionsFloor = {
    chart: {
      type: 'bar',
      stacked: true
    },
    xaxis: {
      categories: categoriesfloor,
      title: { text: 'Date' }
    },
    yaxis: {
      title: { text: 'Booking Rate (%)' },
      labels: {
        formatter: val => `${val}%`
      },
      max: 100
    },
    tooltip: {
      y: {
        formatter: val => `${val}%`
      }
    }
  };

  const disabledDate = date => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return date >= tomorrow;
  };

  return (
    <Panel
      style={{ background: 'none' }}
      header={
        <Stack justifyContent="space-between">
          <h3 className="title">{t('Spaces dashboard')}</h3>
        </Stack>
      }
    >
      <Tabs defaultActiveKey="1" style={{ marginTop: '16px' }}>
        <Tab eventKey="1" title="Past">
          <Stack justifyContent="flex-end" style={{ marginBottom: '16px' }}>
            <Button onClick={() => setShowCalendar(true)} style={{ backgroundColor: '#ffffff' }}>
              <LuCalendarDays style={{ marginRight: 7 }} />
              {t(format(new Date(selectedWeek[0]), 'EEE dd MMM'))} -{' '}
              {t(format(new Date(selectedWeek[6]), 'EEE dd MMM'))}
            </Button>
          </Stack>
          <Modal open={showCalendar} onClose={() => setShowCalendar(false)}>
            <Modal.Header>
              <Modal.Title>{t('Choose a Week')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Calendar
                onSelect={date => {
                  const week = generateWeek(date);
                  setSelectedWeek(week);
                  setShowCalendar(false);
                }}
                compact
                weekStart={1}
                showWeekNumbers
                disabledDate={disabledDate}
              />
            </Modal.Body>
          </Modal>

          <Row gutter={24}>
            <Panel
              className="chart-panel"
              header={t('Booking without check in By Floor')}
              style={{ marginBottom: '16px' }}
            >
              {deskStatsLoading ? (
                <Placeholder.Paragraph rows={6} active />
              ) : (
                <Chart
                  options={deskStats.options}
                  series={deskStats.bookingSeries}
                  type="bar"
                  height={'auto'}
                />
              )}
            </Panel>
            <Panel
              className="chart-panel"
              header={t('Booking with check in by Floor')}
              style={{ marginBottom: '16px' }}
            >
              {deskStatsLoading ? (
                <Placeholder.Paragraph rows={6} active />
              ) : (
                <Chart
                  options={deskStats.options}
                  series={deskStats.occupationSeries}
                  type="bar"
                  height={'auto'}
                />
              )}
            </Panel>
          </Row>
          <Row gutter={24}>
            <Panel
              className="chart-panel mt-4"
              header={t('Booking Rate by Zone')}
              style={{ marginBottom: '16px' }}
            >
              <Stack justifyContent="flex-end" style={{ marginBottom: '16px' }}>
                <SelectPicker
                  data={[
                    { label: 'All Zones', value: null },
                    ...Array.from(new Set(heatmapBookingSeries.map(s => s.name))).map(z => ({
                      label: z,
                      value: z
                    }))
                  ]}
                  value={selectedZone}
                  onChange={setSelectedZone}
                  placeholder="Filter by zone"
                  style={{ width: 240 }}
                  cleanable
                />
              </Stack>
              {loading ? (
                <Placeholder.Paragraph rows={6} active />
              ) : (
                <Chart
                  options={heatmapOptions}
                  series={
                    selectedZone
                      ? heatmapBookingSeries.filter(s => s.name === selectedZone)
                      : heatmapBookingSeries
                  }
                  type="heatmap"
                  height={'auto'}
                />
              )}
            </Panel>
            <Panel
              className="chart-panel mt-4"
              header={t('Occupation Rate by Zone')}
              style={{ marginBottom: '16px' }}
            >
              <Stack justifyContent="flex-end" style={{ marginBottom: '16px' }}>
                <SelectPicker
                  data={[
                    { label: 'All Zones', value: null },
                    ...Array.from(new Set(heatmapBookingSeries.map(s => s.name))).map(z => ({
                      label: z,
                      value: z
                    }))
                  ]}
                  value={selectedZone}
                  onChange={setSelectedZone}
                  placeholder="Filter by zone"
                  style={{ width: 240 }}
                  cleanable
                />
              </Stack>
              {loading ? (
                <Placeholder.Paragraph rows={6} active />
              ) : (
                <Chart
                  options={heatmapOptions}
                  series={
                    selectedZone
                      ? heatmapOccupationSeries.filter(s => s.name === selectedZone)
                      : heatmapOccupationSeries
                  }
                  type="heatmap"
                  height={'auto'}
                />
              )}
            </Panel>
          </Row>
        </Tab>
        <Tab eventKey="2" title="Future">
          <Row gutter={24}>
            <Panel
              header="Future Zone Occupation"
              bordered
              shaded
              className="mt-4"
              style={{ marginBottom: '16px', backgroundColor: '#ffffff' }}
            >
              {loading ? (
                <Placeholder.Paragraph rows={6} active />
              ) : (
                <>
                  {data.length > 0 && (
                    <ButtonGroup style={{ marginBottom: 16, flexWrap: 'wrap' }}>
                      {data.map(f => (
                        <Button
                          key={f.floor}
                          appearance={f.floor === selectedFloor ? 'primary' : 'subtle'}
                          onClick={() => {
                            console.log(' Floor clicked:', f.floor);
                            setSelectedFloor(f.floor);
                          }}
                        >
                          {f.floor}
                        </Button>
                      ))}
                    </ButtonGroup>
                  )}
                  {series.length > 0 ? (
                    <Chart options={options} series={series} type="line" height={350} />
                  ) : (
                    <div style={{ textAlign: 'center', color: '#999' }}>
                      No data available for this floor
                    </div>
                  )}
                </>
              )}
            </Panel>
            <Panel
              header="Future Floor Booking"
              bordered
              shaded
              className="mt-4"
              style={{ marginBottom: '16px', backgroundColor: '#ffffff' }}
            >
              {floorDataLoading ? (
                <Placeholder.Paragraph rows={6} active />
              ) : (
                <Chart options={optionsFloor} series={seriesfloor} type="bar" height={350} />
              )}
            </Panel>
          </Row>
        </Tab>
      </Tabs>
    </Panel>
  );
};

export default ReportingDashboard;
